import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { CustomFieldDef, ZoneAnomalyBlockRow, ZoneAnomalyStatus } from "@/api/reports";
import { Skeleton } from "@/components/Skeleton";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/Table";
import { localizedName } from "@/lib/localizedField";
import { downloadCsv, toCsv, type CsvCell } from "@/lib/csv";
import { useZoneAnomalyReport } from "@/queries/reports";

import { customCsvCells, customCsvHeaders, fieldsParam } from "../customFields";
import type { ReportProps } from "../registry";
import { CustomBodyCells, CustomHeaderCells } from "./CustomColumns";
import { CustomFieldPicker } from "./CustomFieldPicker";
import { ReportIndexSelect } from "./ReportIndexSelect";
import { ReportShell } from "./ReportShell";

const STATUS_CHIP: Record<ZoneAnomalyStatus, string> = {
  anomalies: "bg-ap-crit-soft text-ap-crit",
  clear: "bg-ap-primary-soft text-ap-primary",
  insufficient: "bg-ap-warn-soft text-ap-warn",
  no_data: "bg-ap-bg text-ap-muted",
  no_grid: "bg-ap-bg text-ap-muted",
};

function fmt(value: string | null, digits = 3): string {
  if (value === null) return "—";
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(digits) : "—";
}

export function ZoneAnomalyReport({ farmId, since, until }: ReportProps): ReactNode {
  const { t, i18n } = useTranslation("reports");
  const [indexCode, setIndexCode] = useState("ndvi");
  const [fieldKeys, setFieldKeys] = useState<string[]>([]);
  const fields = fieldsParam(fieldKeys);
  const { data, isLoading, isError } = useZoneAnomalyReport(farmId, {
    index_code: indexCode,
    since,
    until,
    ...(fields ? { fields } : {}),
  });
  const customFields = data?.custom_fields ?? [];

  const handleExport = (): void => {
    if (!data) return;
    const headers = [
      t("zoneAnomaly.headers.block"),
      t("zoneAnomaly.headers.status"),
      t("zoneAnomaly.headers.scene"),
      t("zoneAnomaly.headers.cells"),
      t("zoneAnomaly.headers.flagged"),
      t("zoneAnomaly.headers.area"),
      t("zoneAnomaly.headers.worstZ"),
      t("zoneAnomaly.headers.mean"),
      t("zoneAnomaly.headers.std"),
      t("zoneAnomaly.headers.threshold"),
      ...customCsvHeaders(customFields, i18n.language),
    ];
    const rows: CsvCell[][] = data.blocks.map((b) => [
      localizedName(i18n.language, b.block_name, b.block_name_ar),
      t(`zoneAnomaly.status.${b.status}`),
      b.scene_time?.slice(0, 10) ?? "",
      b.cell_count,
      b.flagged_count,
      b.flagged_area_ha ?? "",
      b.worst_z ?? "",
      b.block_mean ?? "",
      b.block_std ?? "",
      b.threshold_k ?? "",
      ...customCsvCells(customFields, b.custom, i18n.language),
    ]);
    downloadCsv(
      `zone-anomaly_${indexCode}_${since.slice(0, 10)}_${until.slice(0, 10)}`,
      toCsv(headers, rows),
    );
  };

  return (
    <ReportShell
      title={t("catalog.zone-anomaly.title")}
      farmName={data ? localizedName(i18n.language, data.farm_name, data.farm_name_ar) : undefined}
      period={{ since, until }}
      onExportCsv={data ? handleExport : undefined}
    >
      <div className="print-hide mb-4 flex flex-wrap items-center gap-4">
        <ReportIndexSelect value={indexCode} onChange={setIndexCode} />
        <CustomFieldPicker farmId={farmId} value={fieldKeys} onChange={setFieldKeys} />
      </div>

      {isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : isError ? (
        <p className="py-8 text-center text-sm text-ap-crit">{t("loadFailed")}</p>
      ) : !data || data.blocks.length === 0 ? (
        <p className="py-8 text-center text-sm text-ap-muted">{t("zoneAnomaly.empty")}</p>
      ) : (
        <>
          <Summary data={data.summary} />
          <ZoneAnomalyTable rows={data.blocks} customFields={customFields} />
        </>
      )}
    </ReportShell>
  );
}

function Summary({ data }: { data: import("@/api/reports").ZoneAnomalySummary }): ReactNode {
  const { t } = useTranslation("reports");
  return (
    <div className="mb-4 flex flex-wrap items-center gap-4 text-sm">
      <span className="flex items-baseline gap-1.5">
        <span className="text-lg font-semibold tabular-nums text-ap-crit">
          {data.blocks_with_anomalies}
        </span>
        <span className="text-ap-muted">{t("zoneAnomaly.summary.blocksFlagged")}</span>
      </span>
      <span className="flex items-baseline gap-1.5">
        <span className="text-lg font-semibold tabular-nums text-ap-ink">
          {data.total_flagged_cells}
        </span>
        <span className="text-ap-muted">{t("zoneAnomaly.summary.cells")}</span>
      </span>
      <span className="flex items-baseline gap-1.5">
        <span className="text-lg font-semibold tabular-nums text-ap-ink">
          {data.total_flagged_area_ha ? Number(data.total_flagged_area_ha).toFixed(2) : "0.00"}
        </span>
        <span className="text-ap-muted">{t("zoneAnomaly.summary.hectares")}</span>
      </span>
      <span className="ms-auto text-xs text-ap-muted">
        {t("zoneAnomaly.summary.coverage", {
          withGrid: data.blocks_with_grid,
          total: data.block_count,
        })}
      </span>
    </div>
  );
}

function ZoneAnomalyTable({
  rows,
  customFields,
}: {
  rows: ZoneAnomalyBlockRow[];
  customFields: CustomFieldDef[];
}): ReactNode {
  const { t, i18n } = useTranslation("reports");
  return (
    <div className="overflow-x-auto">
      <Table>
        <Thead className="text-[11px]">
          <Tr>
            <Th scope="col">{t("zoneAnomaly.headers.block")}</Th>
            <Th scope="col">{t("zoneAnomaly.headers.status")}</Th>
            <Th scope="col" className="text-end">
              {t("zoneAnomaly.headers.scene")}
            </Th>
            <Th scope="col" className="text-end">
              {t("zoneAnomaly.headers.cells")}
            </Th>
            <Th scope="col" className="text-end">
              {t("zoneAnomaly.headers.flagged")}
            </Th>
            <Th scope="col" className="text-end">
              {t("zoneAnomaly.headers.area")}
            </Th>
            <Th scope="col" className="text-end">
              {t("zoneAnomaly.headers.worstZ")}
            </Th>
            <Th scope="col" className="text-end">
              {t("zoneAnomaly.headers.meanStd")}
            </Th>
            <CustomHeaderCells defs={customFields} />
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((b) => (
            <Tr key={b.block_id} className="hover:bg-ap-bg/40">
              <Td className="font-medium text-ap-ink">
                {localizedName(i18n.language, b.block_name, b.block_name_ar)}
              </Td>
              <Td>
                <span
                  className={`inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium ${STATUS_CHIP[b.status]}`}
                >
                  {t(`zoneAnomaly.status.${b.status}`)}
                </span>
              </Td>
              <Td className="text-end text-[11px]">{b.scene_time?.slice(0, 10) ?? "—"}</Td>
              <Td className="text-end tabular-nums">{b.cell_count || "—"}</Td>
              <Td className="text-end tabular-nums">
                {b.flagged_count > 0 ? (
                  <span className="font-medium text-ap-crit">{b.flagged_count}</span>
                ) : (
                  <span className="text-ap-muted">{b.cell_count ? 0 : "—"}</span>
                )}
              </Td>
              <Td className="text-end tabular-nums text-ap-ink">
                {b.flagged_area_ha ? `${Number(b.flagged_area_ha).toFixed(2)} ha` : "—"}
              </Td>
              <Td className="text-end tabular-nums">
                {b.worst_z !== null ? (
                  <span className={Number(b.worst_z) <= -2 ? "text-ap-crit" : "text-ap-ink"}>
                    {fmt(b.worst_z, 2)}
                  </span>
                ) : (
                  "—"
                )}
              </Td>
              <Td className="text-end tabular-nums">
                {b.block_mean !== null ? `${fmt(b.block_mean)} ± ${fmt(b.block_std)}` : "—"}
              </Td>
              <CustomBodyCells defs={customFields} cells={b.custom} />
            </Tr>
          ))}
        </Tbody>
      </Table>
    </div>
  );
}
